import React, { useState, useEffect } from 'react';
import {
    Card, CardContent, Typography, List, ListItem, ListItemText,
    IconButton, Chip, Box, Button, Alert, CircularProgress, Divider, Grid
} from '@mui/material';
import { Build, CheckCircle, PriorityHigh, Warning, Visibility, EditCalendar } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useAxiosPrivate from '../hooks/useAxiosPrivate';

const MaintenanceWidget = () => {
    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadNotifications();
    }, []);

    const loadNotifications = async () => {
        try {
            setLoading(true);
            const response = await axiosPrivate.get('/maintenance/notifications');
            setNotifications(response.data);
        } catch (err) {
            console.error('Errore nel caricamento delle notifiche manutenzione:', err);
            setError('Impossibile caricare le notifiche');
        } finally {
            setLoading(false);
        }
    };

    const handleDismiss = async (notificationId) => {
        try {
            await axiosPrivate.put(`/maintenance/notifications/${notificationId}/dismiss`);
            // Rimuovi la notifica dalla lista
            setNotifications(notifications.filter(n => n.notification_id !== notificationId));
        } catch (err) {
            console.error('Errore nella dismissione della notifica:', err);
            alert('Errore nell\'aggiornamento della notifica');
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'urgente': return '#d32f2f';
            case 'alta': return '#f57c00';
            case 'media': return '#fbc02d';
            case 'bassa': return '#388e3c';
            default: return '#9e9e9e';
        }
    };

    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'urgente': return <PriorityHigh sx={{ color: '#d32f2f' }} />;
            case 'alta': return <Warning sx={{ color: '#f57c00' }} />;
            default: return <Build />;
        }
    };

    const categoryIcons = {
        idraulica: '💧',
        elettrica: '⚡',
        riscaldamento: '🔥',
        climatizzazione: '❄️',
        serramenti: '🚪',
        tinteggiatura: '🎨',
        giardino: '🌳',
        attrezzature_mediche: '🏥',
        pulizie: '🧹',
        disinfestazione: '🐛',
        altro: '🔧'
    };

    if (loading) {
        return (
            <Card sx={{ p: 3, height: '100%', minHeight: 400 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Build />
                    <Typography variant="h6" fontWeight="bold">Manutenzioni in Arrivo</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={30} />
                </Box>
            </Card>
        );
    }

    if (error) {
        return (
            <Card sx={{ p: 3, height: '100%', minHeight: 400 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Build />
                    <Typography variant="h6" fontWeight="bold">Manutenzioni in Arrivo</Typography>
                </Box>
                <Alert severity="error">{error}</Alert>
            </Card>
        );
    }

    // Limita a massimo 3 notifiche
    const displayedNotifications = notifications.slice(0, 3);

    return (
        <Card sx={{ p: 3, height: '100%', minHeight: 400 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                    Manutenzioni in Arrivo
                    {notifications.length > 0 && (
                        <Chip
                            label={notifications.length}
                            color="warning"
                            size="small"
                            sx={{ ml: 1 }}
                        />
                    )}
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditCalendar />}
                    onClick={() => navigate(`${process.env.REACT_APP_BASE_PATH || '/dashboard'}/maintenance`)}
                >
                    Vedi tutte
                </Button>
            </Box>

            {displayedNotifications.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Build sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                        Nessuna manutenzione programmata
                    </Typography>
                    <Button
                        variant="contained"
                        size="small"
                        sx={{ mt: 2 }}
                        onClick={() => navigate(`${process.env.REACT_APP_BASE_PATH || '/dashboard'}/maintenance`)}
                    >
                        Crea Manutenzione
                    </Button>
                </Box>
            ) : (
                <Box>
                    {displayedNotifications.map((notification) => (
                        <Card
                            key={notification.notification_id}
                            sx={{
                                mb: 2,
                                p: 2,
                                backgroundColor: 'action.hover',
                                '&:hover': { backgroundColor: 'action.selected', cursor: 'pointer' }
                            }}
                            onClick={() => navigate(`${process.env.REACT_APP_BASE_PATH || '/dashboard'}/maintenance`)}
                        >
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{ minWidth: 70 }}>
                                    <Typography variant="body2" color="primary" fontWeight="bold">
                                        Domani
                                    </Typography>
                                    {notification.scheduled_time && (
                                        <Typography variant="caption" color="text.secondary">
                                            {notification.scheduled_time.substring(0, 5)}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" fontWeight="medium" noWrap>
                                        {categoryIcons[notification.category]} {notification.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {notification.priority.charAt(0).toUpperCase() + notification.priority.slice(1)}
                                    </Typography>
                                    {notification.location && (
                                        <Typography variant="caption" display="block" color="text.secondary" noWrap>
                                            📍 {notification.location}
                                        </Typography>
                                    )}
                                    {notification.company_name && (
                                        <Typography variant="caption" display="block" color="text.secondary" noWrap>
                                            🔧 {notification.company_name}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Card>
                    ))}
                </Box>
            )}
        </Card>
    );
};

export default MaintenanceWidget;
